import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
} from "@mui/material";
import { CiEdit } from "react-icons/ci";
import {
  getTrainingActivityRequest,
  getTrainingsRequest,
  saveTrainingRequest,
  updateTrainingStatusRequest,
} from "../../../redux/Training/trainingSlice";
import TableCompo from "../Table/TableCompo";
import { canShowPermission } from "../Permissions/permissionObject";
import { fetchInstalaxmiAdminRequest } from "../../../redux/Tickets/ticketSlice";
import { fetchJewellersSidebarRequest } from "../../../redux/JewellerList/JewellerSlice";
import Autocomplete from "@mui/material/Autocomplete";
import { toast } from "react-toastify";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";

const Training = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const TrainingState = useSelector((state) => state.training || {});
  const { instalaxmiAdmin } = useSelector((state) => state.tickets);

  const { trainingData = [], total = 0, trainingActivity = [] } = TrainingState;
  const { jewellersSidebar = [], loading } = useSelector(
    (state) => state.jeweller,
  );
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [selectedTrainingActivity, setSelectedTrainingActivity] =
    useState(null);
  const [permissions, setPermissions] = useState({});
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  const [pagination, setPagination] = useState({
    page: 0,
    limit: 10,
  });

  const [statusFilter, setStatusFilter] = useState("");
  const [startDateFilter, setStartDateFilter] = useState("");
  const [endDateFilter, setEndDateFilter] = useState("");
  const [search, setSearch] = useState("");

  const [training, setTraining] = useState({
    jeweller: "",
    user: "",
    scheduleDate: "",
    description: "",
    startDate: "",
    completeDate: "",
  });

  useEffect(() => {
    dispatch(
      getTrainingsRequest({
        page: pagination.page + 1,
        limit: pagination.limit,
        status: statusFilter,
        search,
        startDate: startDateFilter ? new Date(startDateFilter).getTime() : "",
        endDate: endDateFilter ? new Date(endDateFilter).getTime() : "",
      }),
    );
  }, [
    dispatch,
    pagination,
    statusFilter,
    search,
    startDateFilter,
    endDateFilter,
  ]);
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");

    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);
  const canEdit = canShowPermission(permissions, "training", "edit");
  const canCreate = canShowPermission(permissions, "training", "create");
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const jewellerID = storedUser?.jeweller;

  useEffect(() => {
    dispatch(fetchInstalaxmiAdminRequest(jewellerID));
  }, [dispatch, jewellerID]);

  useEffect(() => {
    dispatch(fetchJewellersSidebarRequest());
  }, [dispatch]);

  const handleStatusUpdate = () => {
    console.log("selectedTraining", selectedTraining);
    if (!selectedTraining || !selectedStatus) return;

    dispatch(
      updateTrainingStatusRequest({
        id: selectedTraining._id,
        status: selectedStatus,
      }),
    );

    setStatusDialogOpen(false);
  };

  const handleViewActivity = (row) => {
    setSelectedTrainingActivity(row);

    dispatch(
      getTrainingActivityRequest({
        id: row._id,
      }),
    );

    setActivityDialogOpen(true);
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Created At",
      render: (_, row) => new Date(row.createdAt).toLocaleString("en-IN"),
    },
    {
      field: "jeweller",
      headerName: "Jeweller",
      render: (_, row) => row?.jeweller?.jewellerName || "-",
    },
    {
      field: "user",
      headerName: "User",
      render: (_, row) => row?.user?.name || "-",
    },
    {
      field: "scheduleDate",
      headerName: "Schedule Date",
      render: (_, row) =>
        row.scheduleDate
          ? new Date(row.scheduleDate).toLocaleDateString("en-IN")
          : "-",
    },
    {
      field: "startDate",
      headerName: "Start Date",
      render: (_, row) =>
        row.startDate
          ? new Date(row.startDate).toLocaleDateString("en-IN")
          : "-",
    },
    {
      field: "completeDate",
      headerName: "Completed Date",
      render: (_, row) =>
        row.completeDate
          ? new Date(row.completeDate).toLocaleDateString("en-IN")
          : "-",
    },
    {
      field: "status",
      headerName: "Status",
      render: (_, row) => (
        <Button
          variant="contained"
          color={
            row.status === "Completed"
              ? "success"
              : row.status === "InProgress"
                ? "warning"
                : row.status === "Cancelled"
                  ? "error"
                  : "primary"
          }
          onClick={() => {
            setSelectedTraining(row);
            setSelectedStatus(row.status);
            setStatusDialogOpen(true);
          }}
        >
          {row.status}
        </Button>
      ),
    },
    {
      field: "scheduledBy",
      headerName: "Scheduled By",
      render: (_, row) => row?.scheduledBy?.name || "-",
    },
    {
      field: "description",
      headerName: "Description",
      render: (_, row) => row?.description || "-",
    },
    {
      field: "action",
      headerName: "Action",
      render: (_, row) => (
        <CiEdit
          style={{
            cursor: "pointer",
            fontSize: "24px",
          }}
          onClick={() => openEdit(row)}
        />
      ),
    },
    {
      field: "activity",
      headerName: "Activity",
      render: (_, row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleViewActivity(row)}
        >
          View
        </Button>
      ),
    },
  ];

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case "changePage":
        setPagination((prev) => ({
          ...prev,
          page: tableState.page,
        }));
        break;

      case "changeRowsPerPage":
        setPagination({
          page: 0,
          limit: tableState.rowsPerPage,
        });
        break;

      case "search":
        setSearch(tableState.searchText || "");

        setPagination((prev) => ({
          ...prev,
          page: 0,
        }));
        break;

      default:
        break;
    }
  };

  const openCreate = () => {
    setEditMode(false);

    setTraining({
      jeweller: "",
      user: "",
      scheduleDate: "",
      description: "",
      startDate: "",
      completeDate: "",
    });

    setOpen(true);
  };

  const openEdit = (data) => {
    setEditMode(true);

    setTraining({
      ...data,
      jeweller: data?.jeweller?._id || "",
      user: data?.user?._id || "",
      scheduleDate: data.scheduleDate
        ? new Date(data.scheduleDate).toISOString().split("T")[0]
        : "",
      startDate: data?.startDate
        ? new Date(data.startDate).toISOString().split("T")[0]
        : "",
      completeDate: data?.completeDate
        ? new Date(data.completeDate).toISOString().split("T")[0]
        : "",
    });

    setOpen(true);
  };

  const handleSubmit = () => {
    if (!training.jeweller) {
      toast.error("Jeweller Name Required");
      return;
    }

    if (!training.user) {
      toast.error("User Name Required");
      return;
    }

    const payload = {
      jeweller: training.jeweller,
      user: training.user,
      description: training.description,
      scheduleDate: training.scheduleDate
        ? new Date(training.scheduleDate).getTime()
        : null,
      startDate: training.startDate
        ? new Date(training.startDate).getTime()
        : null,

      completeDate: training.completeDate
        ? new Date(training.completeDate).getTime()
        : null,
    };

    if (editMode) {
      dispatch(
        saveTrainingRequest({
          ...payload,
          id: training._id,
        }),
      );
    } else {
      dispatch(saveTrainingRequest(payload));
    }

    setOpen(false);
  };

  const formatActivityValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return String(value);
    }

    // Check if value is timestamp
    if (
      typeof value === "number" ||
      (typeof value === "string" && /^\d{13}$/.test(value))
    ) {
      const date = new Date(Number(value));

      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-IN");
      }
    }

    return String(value);
  };

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTrainingCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/training/getTrainingsCsv",
        query: {},
        callback: (response) => {
          console.log("Store CSV response:", response);

          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "training.csv");
        },
      }),
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          marginTop: "70px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography fontWeight={700} fontSize={24} p={2}>
            Training Management
          </Typography>

          <Button variant="contained" sx={{ mr: 2 }} onClick={openCreate}>
            Schedule Training
          </Button>
        </Box>

        <CardContent>
          <Box mb={2} display="flex" gap={1}>
            <Button
              variant={statusFilter === "" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("")}
            >
              All
            </Button>

            <Button
              variant={statusFilter === "Scheduled" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("Scheduled")}
            >
              Scheduled
            </Button>

            <Button
              variant={statusFilter === "InProgress" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("InProgress")}
            >
              In Progress
            </Button>

            <Button
              variant={statusFilter === "Completed" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("Completed")}
            >
              Completed
            </Button>
            <Button
              variant={statusFilter === "Cancelled" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("Cancelled")}
            >
              Cancelled
            </Button>
          </Box>

          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <TextField
              type="date"
              label="Start Date"
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              value={startDateFilter}
              onChange={(e) => setStartDateFilter(e.target.value)}
            />

            <TextField
              type="date"
              label="End Date"
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              value={endDateFilter}
              onChange={(e) => setEndDateFilter(e.target.value)}
            />
          </Box>

          <TableCompo
            columns={columns}
            data={trainingData}
            totalCount={trainingData.length}
            page={pagination.page}
            limit={pagination.limit}
            onTableChange={handleTableChange}
            extraButtons={
              <Button
                size="sm"
                variant="outlined"
                onClick={handleDownloadTrainingCsv}
              >
                Download CSV
              </Button>
            }
          />
        </CardContent>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>
          {editMode ? "Edit Training" : "Schedule Training"}
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              background: "#f8f9fc",
              px: 2,
              py: 1,
              borderRadius: "14px",
              border: "1px solid #e0e0e0",
              mt: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "#555",
                whiteSpace: "nowrap",
              }}
            >
              Select Jeweller
            </Typography>

            <Autocomplete
              size="small"
              options={jewellersSidebar || []}
              getOptionLabel={(option) => option?.jewellerName || ""}
              value={
                jewellersSidebar.find(
                  (item) => item.id === training.jeweller,
                ) || null
              }
              onChange={(event, newValue) => {
                setTraining({
                  ...training,
                  jeweller: newValue?.id || "",
                });
              }}
              sx={{
                minWidth: 350,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  height: 40,
                  backgroundColor: "#fff",
                  "& fieldset": {
                    borderColor: "#d0d7de",
                  },
                  "&:hover fieldset": {
                    borderColor: "#752ADA",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#752ADA",
                  },
                },
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="Search jeweller..." />
              )}
            />
          </Box>

          <TextField
            fullWidth
            select
            margin="normal"
            label="User"
            value={training.user}
            onChange={(e) => setTraining({ ...training, user: e.target.value })}
          >
            <MenuItem value="">Select User</MenuItem>

            {instalaxmiAdmin?.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user.name}
              </MenuItem>
            ))}
          </TextField>

          {editMode && (
            <>
              <TextField
                type="date"
                fullWidth
                margin="normal"
                label="Schedule Date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={training.scheduleDate}
                onChange={(e) =>
                  setTraining({
                    ...training,
                    scheduleDate: e.target.value,
                  })
                }
              />

              <TextField
                type="date"
                fullWidth
                margin="normal"
                label="Start Date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={training.startDate}
                onChange={(e) =>
                  setTraining({
                    ...training,
                    startDate: e.target.value,
                  })
                }
              />

              <TextField
                type="date"
                fullWidth
                margin="normal"
                label="Completed Date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={training.completeDate}
                onChange={(e) =>
                  setTraining({
                    ...training,
                    completeDate: e.target.value,
                  })
                }
              />
            </>
          )}

          <TextField
            multiline
            rows={4}
            fullWidth
            margin="normal"
            label="Description"
            value={training.description}
            onChange={(e) =>
              setTraining({
                ...training,
                description: e.target.value,
              })
            }
          />

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleSubmit}
          >
            {editMode ? "Update Training" : "Create Training"}
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      >
        <DialogTitle>Change Training Status</DialogTitle>

        <DialogContent>
          <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
            {["Scheduled", "InProgress", "Completed", "Cancelled"].map(
              (status) => (
                <Button
                  key={status}
                  variant={selectedStatus === status ? "contained" : "outlined"}
                  color={
                    status === "Completed"
                      ? "success"
                      : status === "InProgress"
                        ? "warning"
                        : status === "Cancelled"
                          ? "error"
                          : "primary"
                  }
                  onClick={() => setSelectedStatus(status)}
                >
                  {status}
                </Button>
              ),
            )}
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>

            <Button variant="contained" onClick={handleStatusUpdate}>
              OK
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={activityDialogOpen}
        onClose={() => setActivityDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            borderBottom: "1px solid #eee",
          }}
        >
          Training Activity History
        </DialogTitle>

        <DialogContent sx={{ py: 2 }}>
          {!trainingActivity?.length ? (
            <Box
              sx={{
                py: 6,
                textAlign: "center",
              }}
            >
              <Typography color="text.secondary">No Activity Found</Typography>
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {trainingActivity.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    pl: 4,
                    pb: 3,
                    borderLeft: "2px solid #e0e0e0",
                    ml: 1,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: -8,
                      top: 2,
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      bgcolor: "#752ADA",
                    }}
                  />

                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "15px",
                    }}
                  >
                    {item.action}
                  </Typography>

                  {item.oldValue !== undefined &&
                    item.newValue !== undefined && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        <strong>Changed:</strong>{" "}
                        {formatActivityValue(item.oldValue)} →{" "}
                        {formatActivityValue(item.newValue)}
                      </Typography>
                    )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    <strong>Updated By:</strong> {item?.updatedBy?.name || "-"}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.updatedAt).toLocaleString("en-IN")}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={() => setActivityDialogOpen(false)}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Training;
